import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import CollapsibleRow from '../../common/CollapsibleRow';
import ReferenceTypeList from './ReferenceTypeList';
import ReferenceTypeListDialog from './ReferenceTypeListDialog';
import { Reference, ReferenceProperty } from './Reference.interface';

const StyledGrid = styled(Grid)`
  .spacer {
    flex: 1;
  }
  .actions {
    padding-left: 10px;
    padding-right: 10px;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`;

const StyledReferenceTypeList = styled(ReferenceTypeList)`
  background-color: #f7f8fc;
`;

export interface ReferenceDialogProps {
  open: boolean;
  title?: string;
  helpMessage?: string;
  type?: Reference;
  onClose?: () => void;
}

const ReferenceDialog: React.FC<ReferenceDialogProps> = (
  props: ReferenceDialogProps
) => {
  const { open, title, helpMessage, type, onClose } = props;
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [viewType, setViewType] = useState<Reference | undefined>();
  const [showHelp, setShowHelp] = useState(true);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg'>
      <DialogTitle>
        {title} Reference
        {!showHelp && (
          <IconButton
            size='small'
            onClick={() => setShowHelp(true)}
            aria-label='Help'
            title='Show Help'
          >
            <HelpOutlineIcon />
          </IconButton>
        )}
        <Collapse in={showHelp}>
          <Alert onClose={() => setShowHelp(false)} severity='info'>
            {helpMessage}
          </Alert>
        </Collapse>
      </DialogTitle>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={1} />
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {type?.props.map((reference: ReferenceProperty) => {
              const isSelected: boolean = selectedId === reference.id;
              return (
                <ReferenceListItem
                  key={reference.id}
                  referenceField={reference}
                  isSelected={isSelected}
                  onClickType={setViewType}
                  onIconClick={() => {
                    setSelectedId(!isSelected ? reference.id : undefined);
                  }}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <StyledGrid container>
        <Grid item className='actions'>
          <Button variant='outlined' onClick={onClose}>
            Close
          </Button>
        </Grid>
      </StyledGrid>
      <ReferenceTypeListDialog
        type={viewType}
        onClickType={setViewType}
        onClose={() => setViewType(undefined)}
      />
    </Dialog>
  );
};

export default ReferenceDialog;

export interface ReferenceListItemProps {
  referenceField: ReferenceProperty;
  isSelected?: boolean;
  onIconClick?: () => void;
  onClickType?: (type: Reference) => void;
}
export const ReferenceListItem: React.FC<ReferenceListItemProps> = (
  props: ReferenceListItemProps
) => {
  const {
    referenceField,
    isSelected = false,
    onIconClick,
    onClickType
  } = props;
  const hasProperties: boolean | undefined =
    referenceField.isArray || typeof referenceField.type !== 'string';

  const type: string | undefined = useMemo(() => {
    if (typeof referenceField.type !== 'string') {
      const type: Reference = referenceField.type;
      if (referenceField.isArray) {
        return `array (${type.typeName})`;
      } else {
        return type.typeName;
      }
    } else {
      const type: string = referenceField.type;
      return type;
    }
  }, [referenceField]);

  return (
    <CollapsibleRow
      colSpan={5}
      open={isSelected}
      disabled={!hasProperties}
      collapsedContent={
        hasProperties && (
          <StyledReferenceTypeList
            show={isSelected}
            onClickType={onClickType}
            referenceType={
              typeof referenceField.type !== 'string'
                ? referenceField.type
                : undefined
            }
          />
        )
      }
      onIconClick={onIconClick}
      rowProps={{
        hover: hasProperties,
        onClick: () => {
          if (hasProperties) {
            onIconClick?.();
          }
        }
      }}
    >
      <TableCell>{referenceField.name}</TableCell>
      <TableCell>{referenceField.id}</TableCell>
      <TableCell>
        <code>{type}</code>
      </TableCell>
    </CollapsibleRow>
  );
};
